import React, { useEffect } from 'react';
import { Navigate, Link, useLocation } from 'react-router-dom';
import { ArrowRight, CheckCircle2, AlertTriangle, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { LandingHeader } from '../components/landing/LandingHeader';
import { CTASection } from '../components/landing/CTASection';
import INDUSTRIES from '../data/industryData';

export default function IndustryPage() {
  const location = useLocation();
  const slug = location.pathname.replace('/', '');
  const data = INDUSTRIES[slug];

  useEffect(() => {
    if (data) {
      document.title = data.seoTitle;
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) { meta = document.createElement('meta'); meta.name = 'description'; document.head.appendChild(meta); }
      meta.content = data.seoDescription;
    }
    window.scrollTo(0, 0);
  }, [data]);

  if (!data) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-white" data-testid={`industry-page-${slug}`}>
      <LandingHeader />

      {/* HERO */}
      <section className="pt-28 pb-12 md:pt-36 md:pb-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight"
            style={{ fontFamily: 'Manrope' }}
            data-testid="industry-hero-title"
          >
            {data.title}
          </h1>
          <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
            {data.subtitle}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white text-base px-8" data-testid="industry-hero-cta">
                Start Free Trial <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
            <Link to="/features">
              <Button size="lg" variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-50 text-base">
                View All Features
              </Button>
            </Link>
          </div>
          <p className="mt-3 text-sm text-slate-400">30-day free trial &middot; Cancel anytime, no questions asked</p>
        </div>
      </section>

      {/* PROBLEMS */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <h2
            className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-10"
            style={{ fontFamily: 'Manrope' }}
          >
            Challenges You Face Every Day
          </h2>
          <div className="grid sm:grid-cols-2 gap-4 md:gap-6">
            {data.problems.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={i} className="flex items-start gap-4 bg-red-50/60 border border-red-100 rounded-xl p-5" data-testid={`industry-problem-${i}`}>
                  <div className="w-9 h-9 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle size={16} className="text-red-500" />
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed">{p.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SOLUTION */}
      <section className="py-12 md:py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2
            className="text-2xl md:text-3xl font-bold text-slate-900 mb-5"
            style={{ fontFamily: 'Manrope' }}
          >
            How ReStaff Helps
          </h2>
          <p className="text-base md:text-lg text-slate-600 leading-relaxed">
            {data.solution}
          </p>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <h2
            className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-10"
            style={{ fontFamily: 'Manrope' }}
          >
            Features Built for Your Industry
          </h2>
          <div className="grid sm:grid-cols-2 gap-5 md:gap-8">
            {data.features.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="bg-white border border-slate-100 rounded-xl p-6 hover:border-orange-200 hover:shadow-sm transition-all" data-testid={`industry-feature-${i}`}>
                  <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center mb-4">
                    <Icon size={20} className="text-orange-500" />
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-2">{f.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* OUTCOMES */}
      <section className="py-12 md:py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4">
          <h2
            className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-10"
            style={{ fontFamily: 'Manrope' }}
          >
            Results You Can Expect
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {data.outcomes.map((o, i) => (
              <div key={i} className="flex items-center gap-3 bg-white border border-slate-100 rounded-xl p-4" data-testid={`industry-outcome-${i}`}>
                <CheckCircle2 size={18} className="text-green-500 flex-shrink-0" />
                <span className="text-sm font-medium text-slate-700">{o}</span>
              </div>
            ))}
          </div>
          <p className="mt-8 text-sm text-slate-500 text-center leading-relaxed">
            ReStaff combines <Link to="/employee-scheduling" className="text-orange-500 hover:underline font-medium">employee scheduling software</Link>, <Link to="/time-tracking" className="text-orange-500 hover:underline font-medium">time tracking</Link>, and <Link to="/timesheet-software" className="text-orange-500 hover:underline font-medium">timesheet software</Link> in one platform — built for businesses that need to manage labor efficiently.
          </p>
        </div>
      </section>

      {/* FAQ */}
      {data.faq && data.faq.length > 0 && (
        <section className="py-12 md:py-20 bg-white">
          <div className="max-w-3xl mx-auto px-4">
            <h2
              className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-10"
              style={{ fontFamily: 'Manrope' }}
            >
              Frequently Asked Questions
            </h2>
            <div className="space-y-3">
              {data.faq.map((item, i) => (
                <details key={i} className="group bg-slate-50 border border-slate-100 rounded-xl" data-testid={`industry-faq-${i}`}>
                  <summary className="flex items-center justify-between cursor-pointer p-5 text-sm font-semibold text-slate-900 select-none">
                    {item.q}
                    <ChevronDown size={16} className="text-slate-400 transition-transform group-open:rotate-180 flex-shrink-0 ml-3" />
                  </summary>
                  <div className="px-5 pb-5 text-sm text-slate-600 leading-relaxed">
                    {item.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <CTASection />
    </div>
  );
}
